/**
 * SRS Data Service - Fetch Real Learning Progress Data
 * Provides services to fetch actual SRS cards and vocabulary progress
 */

import { supabase } from './supabase';
import { useAuthStore } from '@/stores/authStore';

// Helper function to safely get user ID
function getSafeUserId(): string | null {
  const user = useAuthStore.getState().user;
  return user?.id && user.id !== 'undefined' && typeof user.id === 'string' ? user.id : null;
}

// Helper functions removed - using direct database state mapping

export interface SRSLearningWord {
  id: string;
  word: string;
  translation: string;
  currentState: 'NEW' | 'CONSOLIDATION' | 'RETENTION' | 'LAPSED';
  understandingLevel: number; // 0-100 percentage
  easeFactor: number;
  intervalDays: number;
  nextReview: string;
  lastReviewed: string | null;
  totalCorrectUses: number;
  totalIncorrectUses: number;
  successRate: number; // 0-100 percentage
  confidenceScore: number; // 0-100 percentage
  cefrLevel: string;
  partOfSpeech: string;
  contextSentence: string | null;
  repetitionCount: number;
  timesLapsed: number;
  firstEncountered: string;
  qualityHistory: number[];
  responseTimeAvg: number | null;
}

export interface SRSProgressStats {
  totalCards: number;
  newCards: number;
  consolidationCards: number;
  retentionCards: number;
  lapsedCards: number;
  dueToday: number;
  overdue: number;
  averageRetention: number;
  studyStreak: number;
  wordsLearned: number;
  masteredWords: number;
  strugglingWords: number;
}

export interface SRSWordsByLevel {
  beginner: SRSLearningWord[];
  intermediate: SRSLearningWord[];
  advanced: SRSLearningWord[];
  mastered: SRSLearningWord[];
}

// Database response types (from transformation service)
export interface DatabaseSRSStatistics {
  total_cards: number;
  due_today: number;
  mastered_words: number;
  cards_by_state: {
    NEW: number;
    CONSOLIDATION: number;
    RETENTION: number;
  };
  new_words_this_week: number;
  last_updated: string;
  has_data: boolean;
  recommended_action: string;
  recommendation_urgency: string;
}

export interface DatabaseSRSCard {
  id: string;
  content: string;
  content_type: string;
  language: string;
  state: string;
  mastery_level: string;
  easiness_factor: number;
  interval_days: number;
  repetition_count: number;
  next_review_date: string;
  last_reviewed_at: string | null;
  total_correct_uses: number;
  total_incorrect_uses: number;
  context_sentence: string | null;
  english_context_sentence?: string | null;
  success_rate: number;
  quality_score: number;
  average_response_time: number | null;
  first_encountered_at: string;
  created_at: string;
  updated_at: string;
  english_meaning?: string | null;
  spanish_vocabulary?: {
    id: string;
    word: string;
    english_translation: string;
    part_of_speech: string;
    cefr_level: string;
    frequency_rank: number;
    pronunciation_ipa: string | null;
    audio_url: string | null;
    regional_variants: Record<string, any>;
    topic_categories: string[];
    example_sentences: Record<string, any>;
    collocations: string[];
    difficulty_factors: Record<string, any>;
  };
}

export class SRSDataService {
  /**
   * Transforms database statistics response to frontend interface
   */
  static transformStatistics(dbStats: DatabaseSRSStatistics | DatabaseSRSStatistics[]): SRSProgressStats {
    // Handle array input (database sometimes returns array)
    const stats = Array.isArray(dbStats) ? dbStats[0] : dbStats;

    const transformed = {
      totalCards: stats?.total_cards || 0,
      newCards: stats?.cards_by_state?.NEW || 0,
      consolidationCards: stats?.cards_by_state?.CONSOLIDATION || 0,
      retentionCards: stats?.cards_by_state?.RETENTION || 0,
      lapsedCards: 0, // Not returned by new function, set to 0
      dueToday: stats?.due_today || 0,
      overdue: 0, // Not returned by new function, set to 0
      averageRetention: 85, // Not returned by new function, use default
      studyStreak: 0, // Not returned by new function, set to 0
      wordsLearned: (stats?.cards_by_state?.CONSOLIDATION || 0) + (stats?.cards_by_state?.RETENTION || 0),
      masteredWords: stats?.mastered_words || 0,
      strugglingWords: 0 // Not returned by new function, set to 0
    };

    return transformed;
  }

  /**
   * Transforms database card response to frontend learning word interface
   */
  static transformCard(dbCard: DatabaseSRSCard): SRSLearningWord {
    const spanishVocab = dbCard.spanish_vocabulary;

    return {
      id: dbCard.id,
      word: dbCard.content || spanishVocab?.word || 'Unknown',
      translation: dbCard.english_meaning || spanishVocab?.english_translation || 'No translation',
      currentState: this.validateSRSState(dbCard.state),
      understandingLevel: this.calculateUnderstandingLevel(dbCard),
      easeFactor: dbCard.easiness_factor || 2.5,
      intervalDays: dbCard.interval_days || 1,
      nextReview: dbCard.next_review_date,
      lastReviewed: dbCard.last_reviewed_at,
      totalCorrectUses: dbCard.total_correct_uses || 0,
      totalIncorrectUses: dbCard.total_incorrect_uses || 0,
      successRate: Math.round((dbCard.success_rate || 0) * 100),
      confidenceScore: Math.round((dbCard.quality_score || 0) * 20), // Convert 0-5 to 0-100
      cefrLevel: spanishVocab?.cefr_level || 'A1',
      partOfSpeech: spanishVocab?.part_of_speech || 'noun',
      contextSentence: dbCard.context_sentence,
      repetitionCount: dbCard.repetition_count || 0,
      timesLapsed: 0, // Will be calculated from historical data
      firstEncountered: dbCard.first_encountered_at || dbCard.created_at,
      qualityHistory: [], // Will be populated from review history
      responseTimeAvg: dbCard.average_response_time
    };
  }

  /**
   * Validates and normalizes SRS state values
   */
  static validateSRSState(state: string): 'NEW' | 'CONSOLIDATION' | 'RETENTION' | 'LAPSED' {
    const validStates = ['NEW', 'CONSOLIDATION', 'RETENTION', 'LAPSED'];
    const upperState = state?.toUpperCase();

    if (validStates.includes(upperState)) {
      return upperState as 'NEW' | 'CONSOLIDATION' | 'RETENTION' | 'LAPSED';
    }

    // Fallback mapping for mastery levels
    return this.masteryLevelToState(state);
  }

  /**
   * Maps mastery level to SRS state
   */
  static masteryLevelToState(masteryLevel: string): 'NEW' | 'CONSOLIDATION' | 'RETENTION' | 'LAPSED' {
    const level = masteryLevel?.toLowerCase();

    switch (level) {
      case 'new':
      case 'introduced':
      case 'beginner':
        return 'NEW';
      case 'practiced':
      case 'learning':
      case 'intermediate':
      case 'practicing':
        return 'CONSOLIDATION';
      case 'familiar':
      case 'known':
      case 'mastered':
      case 'advanced':
      case 'fluent':
        return 'RETENTION';
      case 'lapsed':
      case 'forgotten':
      case 'struggling':
        return 'LAPSED';
      default:
        console.warn(`Unknown mastery level: ${masteryLevel}, defaulting to NEW`);
        return 'NEW';
    }
  }

  /**
   * Maps SRS state to mastery level
   */
  static stateToMasteryLevel(state: string): string {
    switch (state?.toUpperCase()) {
      case 'NEW':
        return 'new';
      case 'CONSOLIDATION':
        return 'learning';
      case 'RETENTION':
        return 'mastered';
      case 'LAPSED':
        return 'lapsed';
      default:
        return 'new';
    }
  }

  /**
   * Calculates understanding level from database card data
   */
  static calculateUnderstandingLevel(dbCard: DatabaseSRSCard): number {
    // Use success rate if available
    if (dbCard.success_rate !== null && dbCard.success_rate !== undefined) {
      return Math.round(dbCard.success_rate * 100);
    }

    // Fallback to state-based calculation
    switch (dbCard.state?.toUpperCase()) {
      case 'NEW':
        return 20;
      case 'CONSOLIDATION':
        return 50;
      case 'RETENTION':
        return 85;
      case 'LAPSED':
        return 10;
      default:
        return 0;
    }
  }

  /**
   * Fetch words due for review today
   * Uses srs_cards_minimal via RPC for consistent data
   */
  static async fetchDueWords(limit: number = 50): Promise<{ data: SRSLearningWord[] | null; error: any }> {
    try {
      const userId = getSafeUserId();
      if (!userId) {
        return { data: null, error: new Error('Authentication not ready') };
      }

      // Use RPC to fetch from srs_cards_minimal (consistent with useFixedSRSData)
      const { data, error } = await supabase.rpc('get_srs_cards_due_for_review', {
        p_user_id: userId,
        p_language_code: 'es',
        p_limit: limit
      });

      if (error) {
        return { data: null, error };
      }

      // Transform srs_cards_minimal data to SRSLearningWord format
      const transformedData: SRSLearningWord[] = (data || []).map((card: any) => {
        // Map mastery_level to state
        const state = this.mapMasteryLevelToState(card.mastery_level);
        // Derive values from minimal schema
        const easeFactor = 1.3 + (card.mastery_level / 7) * 1.2;
        const successRate = Math.min(100, (card.consecutive_correct || 0) * 20);

        return {
          id: card.id,
          word: card.content,
          translation: card.english_meaning || 'No translation',
          currentState: state,
          understandingLevel: successRate,
          easeFactor: easeFactor,
          intervalDays: Math.pow(2, card.mastery_level),
          nextReview: card.next_review_date,
          lastReviewed: card.last_reviewed_at,
          totalCorrectUses: card.successful_reviews || 0,
          totalIncorrectUses: (card.total_reviews || 0) - (card.successful_reviews || 0),
          successRate: successRate,
          confidenceScore: Math.min(100, card.mastery_level * 14),
          cefrLevel: 'A2',
          partOfSpeech: 'noun',
          contextSentence: null,
          repetitionCount: card.total_reviews || 0,
          timesLapsed: card.mastery_level === 0 && (card.total_reviews || 0) > 0 ? 1 : 0,
          firstEncountered: card.created_at,
          qualityHistory: [],
          responseTimeAvg: null
        };
      });

      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Error in fetchDueWords:', error);
      return { data: null, error };
    }
  }

  /**
   * Map mastery_level (0-7) to legacy state values
   */
  private static mapMasteryLevelToState(masteryLevel: number): 'NEW' | 'CONSOLIDATION' | 'RETENTION' | 'LAPSED' {
    if (masteryLevel === 0) return 'NEW';
    if (masteryLevel <= 2) return 'CONSOLIDATION';
    return 'RETENTION';
  }

}